/**
 * Checks if a slot is suitable based on availability and spacing requirements
 */
function isSlotSuitable (
  slotName: string,
  slotNum: number,
  minRowAbove: number,
  availableSlots: string[],
  usedSlots: Set<string>,
  prefix: string
): boolean {
  // Check if target slot itself is available and not used
  if (!availableSlots.includes(slotName) || usedSlots.has(slotName)) {
    return false;
  }

  /*
   * If minRowAbove is 0 or 1, no spacing check needed
   * (1 row = no slots need to be free, as slots are placed between rows)
   */
  if (!minRowAbove || minRowAbove <= 1) {
    return true;
  }

  /*
   * Calculate required free slots: minRowAbove rows = (minRowAbove - 1) slots
   * Example: minRowAbove = 2 rows → need 1 slot free (slot between row 1 and row 2)
   * Example: minRowAbove = 3 rows → need 2 slots free
   */
  const requiredFreeSlots = minRowAbove - 1;

  /*
   * Check spacing requirement: slots above must be free
   * A slot is "free" if it either:
   *   1. Doesn't exist in availableSlots (no content = free space), OR
   *   2. Exists but hasn't been used yet
   * A slot is "occupied" ONLY if it exists AND is marked as used
   */
  for (let i = 1; i <= requiredFreeSlots; i++) {
    const requiredSlot = `${prefix}_${slotNum - i}`;

    // Check if this slot is occupied (exists in list AND already used)
    if (availableSlots.includes(requiredSlot) && usedSlots.has(requiredSlot)) {
      return false; // Spacing requirement violated
    }
    /*
     * If slot doesn't exist in list, it's free space (OK)
     * If slot exists but not used, it's free (OK)
     */
  }

  return true;
}

/**
 * Marks a slot and its spacing requirements as used
 */
function markSlotsAsUsed (
  slotName: string,
  minRowAbove: number,
  usedSlots: Set<string>,
  prefix: string
): void {
  const slotNum = parseInt(slotName.split('_')[2]);

  // Mark the target slot as used
  usedSlots.add(slotName);

  // If minRowAbove is 0 or 1, no slots above need to be marked
  if (!minRowAbove || minRowAbove <= 1) {
    return;
  }

  // Calculate required free slots: minRowAbove rows = (minRowAbove - 1) slots
  const requiredFreeSlots = minRowAbove - 1;

  // Mark slots above as used (reserved space)
  for (let i = 1; i <= requiredFreeSlots; i++) {
    const reservedSlot = `${prefix}_${slotNum - i}`;
    /*
     * Mark as used regardless of whether it exists in availableSlots
     * This prevents future ads from using this space
     */
    usedSlots.add(reservedSlot);
  }
}

/**
 * Finds a suitable slot based on renderLogic and placement
 */
function findSuitableSlot (
  renderLogic: any,
  placement: 'top' | 'bottom',
  availableSlots: string[],
  usedSlots: Set<string>
): string | null {
  const targetSlotNum = renderLogic.rowAfter;
  const minRowAbove = renderLogic.minRowAbove || 0; // Default to 0 if undefined
  const prefix = placement === 'top' ? 'top_slot' : 'bottom_slot';

  // First attempt: try the exact target slot (if it exists in available list)
  const targetSlot = `${prefix}_${targetSlotNum}`;

  if (isSlotSuitable(targetSlot, targetSlotNum, minRowAbove, availableSlots, usedSlots, prefix)) {
    return targetSlot;
  }

  /*
   * Find next available slot after target (regardless of distance)
   * This handles cases where target slot doesn't exist (e.g., slot_5 missing, use slot_10)
   */
  const sortedSlots = availableSlots
    .filter(slot => slot.startsWith(prefix))
    .map(slot => ({
      name: slot,
      num: parseInt(slot.split('_')[2])
    }))
    .filter(s => s.num > targetSlotNum && !usedSlots.has(s.name))
    .sort((a, b) => a.num - b.num); // Sort ascending to get nearest available first

  for (const slot of sortedSlots) {
    if (isSlotSuitable(slot.name, slot.num, minRowAbove, availableSlots, usedSlots, prefix)) {
      return slot.name;
    }
  }

  return null; // No suitable slot found
}

/**
 * Processes ad configuration and banner configuration into a unified slot-based structure.
 * Merges inline ads and banner cards into slots, with ads taking precedence over banners.
 *
 * @param adConfig - Ad configuration object (can be null/undefined)
 * @param bannerConfig - Banner configuration object (can be null/undefined)
 * @param pageKey - Page identifier (default: 'frontpage')
 * @returns Slot configuration object organized by slot positions
 */
export function processSlotConfig (
  adConfig: any,
  bannerConfig: any,
  topSlots: string[],
  bottomSlots: string[]
): Record<string, any> | null {
  // Validate inputs - return empty structure if both configs are null/undefined
  if (!adConfig && !bannerConfig) { return null; }

  // Get current device type
  const deviceType = getDeviceType();

  // Track used slots to prevent conflicts
  const usedSlots = new Set<string>();

  // Initialize slot map
  const slotMap: Record<string, any> = {};

  // Extract valid ads and banners, tag each with _itemType
  const validAds = (adConfig && Array.isArray(adConfig.inline))
    ? adConfig.inline
      .filter((ad: any) => shouldShowAd(ad))
      .map((ad: any) => ({ ...ad, _itemType: 'ad' }))
    : [];

  const validBanners = (bannerConfig && Array.isArray(bannerConfig.inline))
    ? bannerConfig.inline
      .filter((banner: any) => shouldRenderBannerCard(banner))
      .map((banner: any) => ({ ...banner, _itemType: 'banner' }))
    : [];

  // Merge ads and banners into a single array
  const combinedItems = [...validAds, ...validBanners];

  // Sort combined array by placement first, then by renderLogic values
  combinedItems.sort((a, b) => {
    // First, sort by placement (top before bottom)
    if (a.placement !== b.placement) {
      return a.placement === 'top' ? -1 : 1;
    }

    // Within same placement, sort by rowAfter
    const aLogic = a.renderLogic[deviceType];
    const bLogic = b.renderLogic[deviceType];

    if (aLogic.rowAfter !== bLogic.rowAfter) {
      return aLogic.rowAfter - bLogic.rowAfter;
    }

    // If rowAfter is same, sort by minRowAbove
    const aMinRow = aLogic.minRowAbove || 0;
    const bMinRow = bLogic.minRowAbove || 0;
    return aMinRow - bMinRow;
  });

  // Process combined array in sorted order
  combinedItems.forEach((item: any) => {
    // Check if renderLogic and placement exist
    if (item.renderLogic && item.placement) {
      const renderLogicForDevice = item.renderLogic[deviceType];

      // Validate renderLogic has required fields
      if (!renderLogicForDevice || typeof renderLogicForDevice.rowAfter !== 'number') { return; }

      // Determine which slot array to use based on placement
      const availableSlots = item.placement === 'top' ? topSlots : bottomSlots;

      // Find suitable slot
      const foundSlot = findSuitableSlot(
        renderLogicForDevice,
        item.placement,
        availableSlots,
        usedSlots
      );

      if (foundSlot) {
        // Initialize slot array if needed
        if (!slotMap[foundSlot]) {
          slotMap[foundSlot] = [];
        }

        // Remove renderLogic, placement, and _itemType before adding to slotMap
        const { renderLogic, placement, _itemType, ...itemWithoutMetadata } = item;

        // Add to slot map with appropriate type
        if (_itemType === 'ad') {
          slotMap[foundSlot].push({ type: 'ad', ad: itemWithoutMetadata });
        } else {
          slotMap[foundSlot].push({ type: 'maFrame', data: itemWithoutMetadata });
        }

        // Mark slot as used
        markSlotsAsUsed(
          foundSlot,
          renderLogicForDevice.minRowAbove || 0,
          usedSlots,
          item.placement === 'top' ? 'top_slot' : 'bottom_slot'
        );
      }
    }
  });

  return slotMap;
}
